import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { FaultPhotoPicker, uploadFaultPhotos } from '@/components/fleet/FaultPhotos';
import { Wrench, Plus, Trash2, Truck, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INSPECTION_FIELDS = [
  { key: 'tyres',  label: 'Tyres',  options: ['OK', 'WORN', 'DAMAGED'] },
  { key: 'lights', label: 'Lights', options: ['OK', 'FAULTY'] },
  { key: 'brakes', label: 'Brakes', options: ['OK', 'WORN', 'DAMAGED'] },
  { key: 'fluids', label: 'Fluids', options: ['OK', 'LOW'] },
  { key: 'body',   label: 'Body',   options: ['OK', 'DAMAGE'] },
];

const SEVERITY_COLOURS = {
  critical: 'bg-rose-600',
  high: 'bg-orange-500',
  medium: 'bg-amber-500',
  low: 'bg-slate-400',
};

/**
 * VehicleInspectionReport — Standalone form for staff/drivers to file an
 * inspection report + faults. Each fault auto-creates a Job Card that hits
 * the workshop queue immediately.
 */
export default function VehicleInspectionReport() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [reg, setReg] = useState('');
  const [odo, setOdo] = useState('');
  const [inspNotes, setInspNotes] = useState('');
  const [insp, setInsp] = useState({ tyres: 'OK', lights: 'OK', brakes: 'OK', fluids: 'OK', body: 'OK' });
  const [faults, setFaults] = useState([{ description: '', severity: 'medium' }]);
  const [saving, setSaving] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const addFault = () => setFaults([...faults, { description: '', severity: 'medium' }]);
  const removeFault = (i) => setFaults(faults.filter((_, idx) => idx !== i));
  const setFault = (i, patch) => setFaults(faults.map((f, idx) => idx === i ? { ...f, ...patch } : f));

  const submit = async () => {
    if (!reg.trim()) return toast.error('Vehicle registration required');
    const validFaults = faults.filter((f) => (f.description || '').trim());
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/vehicle-reports`, {
        vehicle_registration: reg.trim().toUpperCase(),
        odometer: odo ? parseInt(odo, 10) : null,
        inspection: { ...insp, notes: inspNotes.trim() },
        faults: validFaults.map(({ photos, ...f }) => f),
      }, { headers });
      setLastReport(res.data);
      if (validFaults.some((f) => (f.photos || []).length)) {
        const r = await uploadFaultPhotos(res.data.fault_ids || [], validFaults, headers);
        if (r.failed) toast.error(`${r.failed} photo(s) failed to upload`);
      }
      const jc = (res.data.job_card_ids || []).length;
      toast.success(`Report filed${jc > 0 ? ` — ${jc} job card${jc === 1 ? '' : 's'} auto-opened for the workshop` : ''}`);
      // Reset form
      setReg(''); setOdo(''); setInspNotes('');
      setInsp({ tyres: 'OK', lights: 'OK', brakes: 'OK', fluids: 'OK', body: 'OK' });
      setFaults([{ description: '', severity: 'medium' }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !lastReport) return;
    setUploadingPhoto(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/vehicle-reports/${lastReport.id}/photo`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setLastReport({ ...lastReport, photos: [...(lastReport.photos || []), res.data] });
      toast.success('Photo attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-4 w-4 text-cyan-600" /> Vehicle
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Registration *</label>
            <Input
              value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase())}
              placeholder="e.g. CFM94394"
              data-testid="vr-reg"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Odometer (optional)</label>
            <Input
              type="number"
              value={odo}
              onChange={(e) => setOdo(e.target.value)}
              placeholder="km"
              data-testid="vr-odo"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Inspection checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {INSPECTION_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <div className="w-24 text-sm font-medium">{f.label}</div>
              <div className="flex gap-2 flex-wrap">
                {f.options.map((opt) => (
                  <Button
                    key={opt}
                    type="button"
                    size="sm"
                    variant={insp[f.key] === opt ? 'default' : 'outline'}
                    className={insp[f.key] === opt && opt !== 'OK' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                    onClick={() => setInsp({ ...insp, [f.key]: opt })}
                    data-testid={`vr-insp-${f.key}-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <label className="text-sm font-medium">Notes (optional)</label>
            <Textarea
              value={inspNotes}
              onChange={(e) => setInspNotes(e.target.value)}
              rows={2}
              placeholder="Anything else the workshop should know…"
              data-testid="vr-insp-notes"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4 text-rose-600" /> Fault reports → Job Cards
          </CardTitle>
          <Button size="sm" variant="outline" onClick={addFault} data-testid="add-fault-btn">
            <Plus className="h-3 w-3 mr-1" /> Add fault
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Each fault below will open a Job Card assigned to the Workshop. Leave blank if there are no faults.
          </p>
          {faults.map((f, i) => (
            <div key={i} className="border rounded p-3 space-y-2" data-testid={`fault-row-${i}`}>
              <div className="flex items-start gap-2">
                <Textarea
                  value={f.description}
                  onChange={(e) => setFault(i, { description: e.target.value })}
                  rows={2}
                  placeholder="Describe the problem — what, where, how bad"
                  className="flex-1"
                  data-testid={`fault-desc-${i}`}
                />
                {faults.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeFault(i)} className="text-rose-600">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <FaultPhotoPicker files={f.photos || []} onChange={(photos) => setFault(i, { photos })} testId={`inspection-fault-photo-${i}`} />
              <div className="flex gap-2 flex-wrap">
                {['critical', 'high', 'medium', 'low'].map((sev) => (
                  <Button
                    key={sev}
                    type="button"
                    size="sm"
                    variant={f.severity === sev ? 'default' : 'outline'}
                    className={f.severity === sev ? SEVERITY_COLOURS[sev] : ''}
                    onClick={() => setFault(i, { severity: sev })}
                    data-testid={`fault-sev-${i}-${sev}`}
                  >
                    {sev}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button
        onClick={submit}
        disabled={saving}
        className="w-full h-12 text-base bg-cyan-600 hover:bg-cyan-700"
        data-testid="vr-submit"
      >
        {saving ? 'Filing report…' : 'Submit inspection & faults'}
      </Button>

      {lastReport && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="vr-success-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-emerald-900 flex items-center gap-2">
              ✓ Report filed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>Vehicle: <b>{lastReport.vehicle_registration}</b></div>
            <div>Job Cards created: <b>{lastReport.job_card_ids?.length || 0}</b></div>
            <div className="flex flex-wrap gap-2">
              {lastReport.job_card_ids?.map((id) => (
                <Badge key={id} variant="outline" className="bg-white">#{id.slice(0, 8)}</Badge>
              ))}
            </div>
            <div className="pt-2">
              <label className="cursor-pointer inline-flex items-center gap-2 text-sm bg-white border rounded px-3 py-1.5 hover:bg-slate-50">
                <Camera className="h-4 w-4" />
                {uploadingPhoto ? 'Uploading…' : 'Attach photo'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploadingPhoto} data-testid="vr-photo-input" />
              </label>
              {lastReport.photos?.length > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">{lastReport.photos.length} photo(s) attached</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
